package com.trevorwiebe.timesheet.admin.reporting.domain

import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.TripType
import com.trevorwiebe.timesheet.mileage.domain.model.TripTypeData
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atTime
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.math.abs
import kotlin.time.Duration.Companion.minutes

data class MissingStartEndFix(
    val entriesBefore: List<MileageEntry> = emptyList(),
    val entriesAfter: List<MileageEntry> = emptyList(),
    val endTemplate: MileageEntry? = null,
    val startTemplate: MileageEntry? = null,
    val prefilledMileage: Long? = null,
    val location: String = "",
)

fun buildMissingStartEndFix(
    boundary: MissingStartEndBoundary,
    entries: List<MileageEntry>,
): MissingStartEndFix {
    val truckEntries = entries
        .filter { it.equipmentId == boundary.truckId }
        .sortedWith(compareBy({ it.date }, { it.mileageValue }))
    val endDate = boundary.endDate
    val startDate = boundary.startDate

    val entriesBefore = truckEntries.filter { entry ->
        if (endDate != null) entry.date.date <= endDate
        else startDate != null && entry.date.date < startDate
    }
    val entriesAfter = truckEntries.filter { entry ->
        if (startDate != null) entry.date.date >= startDate
        else endDate != null && entry.date.date > endDate
    }
    val endDayEntries = if (endDate != null) truckEntries.filter { it.date.date == endDate } else emptyList()
    val startDayEntries = if (startDate != null) truckEntries.filter { it.date.date == startDate } else emptyList()

    val boundaryDate = endDate ?: startDate
    val location = truckEntries
        .filter { it.tripType == TripType.START_END }
        .minByOrNull { entry ->
            boundaryDate?.let { abs(entry.date.date.toEpochDays() - it.toEpochDays()) } ?: 0
        }
        ?.let { (it.tripTypeData as? TripTypeData.StartEnd)?.location }
        ?: ""

    val endTemplate = if (boundary.missingEnd && endDayEntries.isNotEmpty()) {
        val last = endDayEntries.last()
        last.toStartEndTemplate(date = last.date.fiveMinutesAfterWithinDay(), location = location)
    } else null

    val startTemplate = if (boundary.missingStart && startDayEntries.isNotEmpty()) {
        val first = startDayEntries.first()
        first.toStartEndTemplate(date = first.date.fiveMinutesBeforeWithinDay(), location = location)
    } else null

    val prefilledMileage = when {
        !boundary.missingEnd && endDayEntries.isNotEmpty() -> endDayEntries.last().mileageValue
        !boundary.missingStart && startDayEntries.isNotEmpty() -> startDayEntries.first().mileageValue
        else -> null
    }

    return MissingStartEndFix(
        entriesBefore = entriesBefore.takeLast(2),
        entriesAfter = entriesAfter.take(2),
        endTemplate = endTemplate,
        startTemplate = startTemplate,
        prefilledMileage = prefilledMileage,
        location = location,
    )
}

private fun MileageEntry.toStartEndTemplate(
    date: LocalDateTime,
    location: String,
) = MileageEntry(
    id = "",
    date = date,
    employeeId = employeeId,
    equipmentId = equipmentId,
    trailerId = trailerId,
    mileageValue = 0,
    tripType = TripType.START_END,
    tripTypeData = TripTypeData.StartEnd(location),
)

private fun LocalDateTime.fiveMinutesBeforeWithinDay(): LocalDateTime {
    val timeZone = TimeZone.currentSystemDefault()
    val shifted = toInstant(timeZone).minus(5.minutes).toLocalDateTime(timeZone)
    return if (shifted.date == date) shifted else date.atTime(0, 0)
}

private fun LocalDateTime.fiveMinutesAfterWithinDay(): LocalDateTime {
    val timeZone = TimeZone.currentSystemDefault()
    val shifted = toInstant(timeZone).plus(5.minutes).toLocalDateTime(timeZone)
    return if (shifted.date == date) shifted else date.atTime(23, 59)
}
